import React from 'react';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error?: Error;
  hasError: boolean;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      error,
      hasError: true,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Keep the full stack in development while preserving a calm patient-facing fallback.
    if (process.env.NODE_ENV !== 'production') {
      console.error('Application error boundary caught an error:', error, errorInfo);
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handlePortalHome = () => {
    window.location.assign('/portal');
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <main className="min-h-screen bg-secondary-50 px-4 py-8 flex items-center justify-center">
        <section className="w-full max-w-xl bg-white rounded-2xl shadow-xl border border-secondary-100 p-6 sm:p-10 text-center animate-fade-in">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500">
            <AlertTriangle size={32} strokeWidth={2.25} />
          </div>

          <p className="text-sm font-semibold uppercase tracking-wide text-teamcare-blue">
            Patient portal
          </p>
          <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-secondary-900">
            Something went wrong
          </h1>
          <p className="mt-4 text-sm sm:text-base leading-7 text-secondary-500">
            We could not load this part of the portal. Please refresh the page, or return to the
            portal home and try again.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-teamcare-blue px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teamcare-blue-dark focus:outline-none focus:ring-2 focus:ring-teamcare-blue focus:ring-offset-2"
            >
              <RefreshCw size={18} />
              Refresh page
            </button>
            <button
              type="button"
              onClick={this.handlePortalHome}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-secondary-200 bg-white px-5 py-3 text-sm font-semibold text-secondary-700 transition-colors hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-teamcare-blue focus:ring-offset-2"
            >
              <Home size={18} />
              Portal home
            </button>
          </div>

          {process.env.NODE_ENV !== 'production' && this.state.error?.message && (
            <pre className="mt-8 max-h-36 overflow-auto rounded-xl bg-secondary-900 p-4 text-left text-xs leading-5 text-secondary-100">
              {this.state.error.message}
            </pre>
          )}
        </section>
      </main>
    );
  }
}
